/**
 * Explainability engine for priority decisions.
 * Provides structured explanations for why items have specific priorities.
 */

import type {
  CanonicalScoreSet,
  CriteriaSet,
  Lens,
  ToolConfig,
} from "../harmonizer/types";
import type { ArrangeAuditEvent } from "./audit";

/**
 * Structured explanation for why an item has a specific priority.
 */
export type PriorityExplanation = {
  itemId: string;
  finalRank: number | null;
  finalScore: number | null;
  finalCategory: string | null;
  toolUsed: string;
  toolConfig: ToolConfig;
  criteriaSet: CriteriaSet | null;
  lens: Lens | null;
  rawInputs: Record<string, any>;
  eventChain: Array<{
    type: ArrangeAuditEvent["type"];
    timestamp: number;
    toolName?: string;
    toolConfig?: ToolConfig;
    criteriaSetId?: string | null;
    lensId?: string | null;
    scoreSet?: CanonicalScoreSet;
    error?: string;
  }>;
};

/**
 * Explains why an item has a specific priority based on score set and audit events.
 * 
 * This function reconstructs the decision-making process by:
 * 1. Finding the item's final score/rank/category from the score set
 * 2. Extracting tool configuration and context from audit events
 * 3. Building a complete event chain showing the decision process
 * 
 * @param itemId - ID of the item to explain
 * @param scoreSet - Final canonical score set
 * @param ledgerEvents - Array of audit events in chronological order
 * @returns PriorityExplanation with complete decision context
 */
export function explainPriority(
  itemId: string,
  scoreSet: CanonicalScoreSet,
  ledgerEvents: ArrangeAuditEvent[]
): PriorityExplanation {
  // Find the item's score in the final score set
  const itemScore = scoreSet.scores.find((s) => s.itemId === itemId);

  // Extract tool configuration and context from the most recent ToolRunCompleted event
  let toolConfig: ToolConfig = {};
  let criteriaSet: CriteriaSet | null = null;
  let lens: Lens | null = null;
  let rawInputs: Record<string, any> = {};

  // Find the most recent ToolRunCompleted event for this score set
  const completedEvent = ledgerEvents
    .filter((e): e is Extract<ArrangeAuditEvent, { type: "ToolRunCompleted" }> =>
      e.type === "ToolRunCompleted" && e.scoreSet.toolRunId === scoreSet.toolRunId
    )
    .sort((a, b) => b.timestamp - a.timestamp)[0];

  if (completedEvent) {
    toolConfig = completedEvent.toolConfig;
    // Note: We don't have the full CriteriaSet or Lens objects in events,
    // only their IDs. For a complete explanation, these would need to be
    // looked up from a store. For now, we'll use null and note the IDs.
    criteriaSet = null; // Would need to be looked up by completedEvent.criteriaSetId
    lens = null; // Would need to be looked up by completedEvent.lensId
    rawInputs = completedEvent.scoreSet.metadata || {};
  }

  // Build event chain for this item
  const eventChain = ledgerEvents
    .filter((e) => {
      // Include events related to this tool run
      if (e.type === "ToolRunStarted" || e.type === "ToolRunCompleted" || e.type === "ToolRunFailed") {
        return e.toolName === scoreSet.toolName;
      }
      if (e.type === "PrioritySnapshotCreated") {
        return e.scoreSet.toolRunId === scoreSet.toolRunId;
      }
      return false;
    })
    .map((e) => {
      const base = {
        type: e.type,
        timestamp: e.timestamp,
      };

      if (e.type === "ToolRunStarted" || e.type === "ToolRunCompleted" || e.type === "ToolRunFailed") {
        return {
          ...base,
          toolName: e.toolName,
          toolConfig: e.toolConfig,
          criteriaSetId: e.criteriaSetId,
          lensId: e.lensId,
          ...(e.type === "ToolRunCompleted" ? { scoreSet: e.scoreSet } : {}),
          ...(e.type === "ToolRunFailed" ? { error: e.error } : {}),
        };
      }

      if (e.type === "PrioritySnapshotCreated") {
        return {
          ...base,
          toolName: e.toolName,
          criteriaSetId: e.criteriaSetId,
          lensId: e.lensId,
          scoreSet: e.scoreSet,
        };
      }

      return base;
    })
    .sort((a, b) => a.timestamp - b.timestamp);

  return {
    itemId,
    finalRank: itemScore?.rank ?? null,
    finalScore: itemScore?.numericScore ?? null,
    finalCategory: itemScore?.categoryId ?? null,
    toolUsed: scoreSet.toolName,
    toolConfig,
    criteriaSet,
    lens,
    rawInputs,
    eventChain,
  };
}

/**
 * Executive view of priority for an item.
 * Provides a high-level summary suitable for executive reporting.
 */
export type ExecutivePriorityView = {
  itemId: string;
  label?: string;
  rank: number | null;
  numericScore: number | null;
  categoryId: string | null;
  toolName: string;
  keyDrivers: string[]; // short labels of top criteria influencing result
};

/**
 * Trace view of priority for an item.
 * Provides complete audit trail and explanation.
 */
export type TracePriorityView = {
  itemId: string;
  toolName: string;
  eventChain: ArrangeAuditEvent[];
  rawExplanation: PriorityExplanation;
};

/**
 * Builds an executive priority view for an item.
 * 
 * Extracts high-level information suitable for executive reporting:
 * - Rank, score, category
 * - Tool used
 * - Key drivers (top criteria/attributes)
 * 
 * @param itemId - ID of the item
 * @param scoreSet - Canonical score set
 * @returns Executive priority view
 */
export function buildExecutivePriorityView(
  itemId: string,
  scoreSet: CanonicalScoreSet
): ExecutivePriorityView {
  const itemScore = scoreSet.scores.find((s) => s.itemId === itemId);

  // Extract key drivers from annotations
  const keyDrivers: string[] = [];
  if (itemScore?.annotations) {
    // Look for criteria-related annotations
    for (const [key, value] of Object.entries(itemScore.annotations)) {
      // Skip internal metadata
      if (key === "tool_name" || key.startsWith("_")) {
        continue;
      }
      
      // Include numeric attributes that likely influenced the score
      if (typeof value === "number") {
        keyDrivers.push(key);
      }
    }
  }

  // If no annotations, try to infer from scoreSet metadata
  if (keyDrivers.length === 0 && scoreSet.metadata) {
    // Look for criteria or attribute references in metadata
    for (const [key, value] of Object.entries(scoreSet.metadata)) {
      if (typeof value === "string" && (key.includes("criteria") || key.includes("attribute"))) {
        keyDrivers.push(value);
      }
    }
  }

  return {
    itemId,
    label: undefined, // Would need to be looked up from items
    rank: itemScore?.rank ?? null,
    numericScore: itemScore?.numericScore ?? null,
    categoryId: itemScore?.categoryId ?? null,
    toolName: scoreSet.toolName,
    keyDrivers: keyDrivers.slice(0, 5), // Limit to top 5
  };
}

/**
 * Builds a trace priority view for an item.
 * 
 * Provides complete audit trail and explanation:
 * - Full event chain
 * - Raw explanation object
 * 
 * @param itemId - ID of the item
 * @param scoreSet - Canonical score set
 * @param ledgerEvents - Array of audit events
 * @returns Trace priority view
 */
export function buildTracePriorityView(
  itemId: string,
  scoreSet: CanonicalScoreSet,
  ledgerEvents: ArrangeAuditEvent[]
): TracePriorityView {
  const rawExplanation = explainPriority(itemId, scoreSet, ledgerEvents);

  return {
    itemId,
    toolName: scoreSet.toolName,
    eventChain: ledgerEvents,
    rawExplanation,
  };
}

